/** Thẻ chọn người phụ trách: trợ lý hỏi khi user chưa nói giao cho ai, hoặc tên trùng ở nhiều phòng (chọn đúng phòng ban). */
import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, Spin, Tag } from "antd";
import { CheckCircleFilled, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { actionPlanV2Api, type AiScopeTreeResponse } from "@/services/action-plan-v2";
import { unwrapApiResponse } from "@/components/common/ActionPlanV2/actionPlanAiConfirm";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import type { ActionPlanAiAssigneeListState } from "@/types/action-plan-v2/ActionPlanAiAssigneeList";
import type { ActionPlanAiScopeEmployee } from "@/types/action-plan-v2/ActionPlanAiScopeList";
import styles from "./ActionPlanAssigneePickerCard.module.css";

type Employee = ActionPlanAiScopeEmployee & { coTheGiao?: boolean };

type Props = {
  state: ActionPlanAiAssigneeListState;
  /** Gửi lựa chọn vào chat — trợ lý chuẩn bị lại thẻ tạo việc với người này. */
  onPick: (picked: { id: string; ten: string; tenBoPhan?: string }) => void;
};

const initials = (ten?: string) => {
  const parts = String(ten || "?").replace(/\(.*?\)/g, "").trim().split(/\s+/);
  return ((parts.length > 1 ? parts[parts.length - 2][0] : "") + (parts[parts.length - 1]?.[0] || "?")).toUpperCase();
};

const normalize = (s?: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

export const ActionPlanAssigneePickerCard: React.FC<Props> = ({ state, onPick }) => {
  const { list, picked } = state;
  const ambiguous = list.reason === "ambiguous";
  const scopeReq = useMemo(
    () => ({
      idChoice: String(list.idChoice || "").trim(),
      year: list.year ?? new Date().getFullYear(),
      phanHe: list.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
    }),
    [list.idChoice, list.phanHe, list.year],
  );
  const deptOptions = useMemo(
    () => list.phongBans.map((d) => ({ value: d.id, label: d.ten || d.id })),
    [list.phongBans],
  );
  // Toàn đơn vị: chọn phòng trước rồi mới tải NV (danh sách toàn site quá lớn)
  const needDept = !ambiguous && list.quyenGhi === "all";
  const [dept, setDept] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [employees, setEmployees] = useState<Employee[]>(list.nhanViens ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ambiguous || (!dept && !(needDept && query.trim().length >= 2))) return;
    let alive = true;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const raw = await actionPlanV2Api.aiScopeTree({
          ...scopeReq,
          boPhanId: dept,
          query: !dept ? query.trim() : undefined,
        });
        const res = unwrapApiResponse<AiScopeTreeResponse>(raw);
        if (alive && res?.success && res.data) {
          setEmployees(((res.data.nhanViens ?? []) as Employee[]).filter((e) => e.coTheGiao !== false));
        }
      } finally {
        if (alive) setLoading(false);
      }
    }, dept ? 0 : 350);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [ambiguous, dept, needDept, query, scopeReq]);

  const shown = useMemo(() => {
    const q = normalize(query.trim());
    const rows = employees.filter((e) => e.coTheGiao !== false);
    const hit = q ? rows.filter((e) => normalize(e.ten).includes(q) || normalize(e.tenBoPhan).includes(q)) : rows;
    // «Bạn» luôn đứng đầu
    return [...hit].sort((a, b) => Number(b.id === list.selfId) - Number(a.id === list.selfId));
  }, [employees, list.selfId, query]);

  /** ↑ ↓ di chuyển giữa các người trong danh sách (Enter / Space bấm chọn — mặc định của button). */
  const onListKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>("button[role=option]"));
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === "ArrowDown" ? Math.min(items.length - 1, i + 1) : Math.max(0, i - 1);
    items[next]?.focus();
  };

  const title = ambiguous ? `Có ${list.nhanViens.length} người tên «${list.hint || ""}»` : "Chọn người phụ trách";
  const sub = ambiguous
    ? "Tên trùng ở nhiều phòng ban — chọn đúng người theo phòng ban."
    : list.quyenGhi === "all"
      ? "Chọn phòng ban hoặc gõ tên để tìm người phụ trách."
      : "Bạn giao được cho chính mình hoặc nhân viên thuộc phòng bạn quản lý.";

  return (
    <section className={styles.card} aria-label={title}>
      <header className={styles.head}>
        <span className={styles.headIcon} aria-hidden>
          <UserOutlined />
        </span>
        <div>
          <div className={styles.headTitle}>{title}</div>
          <div className={styles.headSub}>{sub}</div>
        </div>
      </header>

      {picked ? (
        <div className={styles.done}>
          <CheckCircleFilled />
          <span>
            Đã chọn <b>{picked.ten}</b>
            {picked.tenBoPhan ? ` — ${picked.tenBoPhan}` : ""}
          </span>
        </div>
      ) : (
        <>
          {!ambiguous ? (
            <div className={styles.tools}>
              {deptOptions.length > 1 || needDept ? (
                <Select
                  size="small"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Phòng ban"
                  options={deptOptions}
                  value={dept}
                  onChange={(v) => setDept(v)}
                  aria-label="Chọn phòng ban"
                />
              ) : null}
              <Input
                size="small"
                allowClear
                prefix={<SearchOutlined />}
                placeholder={needDept && !dept ? "Gõ ít nhất 2 ký tự tên…" : "Tìm theo tên"}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Tìm người phụ trách"
              />
            </div>
          ) : null}
          <div className={styles.list} role="listbox" aria-label="Danh sách người phụ trách" onKeyDown={onListKey}>
            {loading ? (
              <div className={styles.empty}>
                <Spin size="small" />
              </div>
            ) : shown.length === 0 ? (
              <div className={styles.empty}>
                {needDept && !dept && query.trim().length < 2 ? "Chọn phòng ban hoặc gõ tên để xem danh sách." : "Không có người phù hợp."}
              </div>
            ) : (
              shown.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className={styles.item}
                  onClick={() => onPick({ id: e.id, ten: e.ten || e.id, tenBoPhan: e.tenBoPhan })}
                >
                  <span className={styles.avatar} aria-hidden>
                    {initials(e.ten)}
                  </span>
                  <span className={styles.itemText}>
                    <span className={styles.itemName}>{e.ten || e.id}</span>
                    <span className={styles.itemMeta}>{e.tenBoPhan || "—"}</span>
                  </span>
                  {e.id === list.selfId ? (
                    <Tag bordered={false} color="blue" className="!m-0 !text-[11px]">
                      Bạn
                    </Tag>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ActionPlanAssigneePickerCard;
